import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { createApiUrl, API_ENDPOINTS } from '../config/api'
import logger from '../utils/logger'
import persistentStorage, { 
  getAuthToken, 
  getUserInfo, 
  setUserInfo, 
  getSelectedFarm, 
  setSelectedFarm as persistSelectedFarm, 
  clearAllAuthData,
  persistSessionTokens
} from '../utils/persistentStorage'
import tokenManager, { handleAppStartup } from '../utils/tokenManager'
import notificationService from '../services/NotificationService'
import currencyService from '../services/currencyService'

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: 'ADMIN' | 'INDIVIDUAL' | 'CORPORATE' | 'SUB_USER'
  corporateRole?: 'MANAGER' | 'MEMBER' // Corporate user role
  // accountType removed - use role instead
  farmId?: string
  parentUserId?: string
  corporateAccountId?: string
  individualAccountId?: string
  allowAdminAccess: boolean
  subscriptionId?: string
  preferredLanguage?: string
  timezone?: string
  name?: string
  farmName?: string
  userType?: string
  active?: boolean
  individualAccount?: {
    id: string
    accountName: string
    isPaidAccount?: boolean
    plan?: {
      id: string
      name: string
      displayName: string
      monthlyPrice: number
    } | null
    availableLanguages?: string[]
    calendarType?: 'GREGORIAN' | 'JALALI'
  }
  corporateAccount?: {
    id: string
    companyName: string
    availableLanguages?: string[]
    calendarType?: 'GREGORIAN' | 'JALALI'
  }
}

interface UserFarm {
  farmId: string
  farmName: string
  accountId: string
  accountName: string
  accessType: string
  grantedBy: {
    firstName: string
    lastName: string
    email: string
  }
  grantedAt: string
}

interface UserContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => void
  selectedFarm: any
  setSelectedFarm: (farm: any) => void
  selectedAdminAccount: any
  setSelectedAdminAccount: (account: any) => void
  selectedCorporateFarm: any
  setSelectedCorporateFarm: (farm: any) => void
  isAdmin: boolean
  isConsultant: boolean
  isFarmer: boolean
  isCorporateManager: boolean
  isCorporateMember: boolean
  isCorporate: boolean
  effectiveFarmId: string | null
  effectiveCorporateAccountId: string | null
  isAdminViewingCorporate: boolean
  refreshUser: () => Promise<void>
  userFarms: UserFarm[]
  isMultiFarmUser: boolean
  fetchUserFarms: () => Promise<void>
  farmCurrencyCode: string
  exchangeRate: number | null
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedFarm, setSelectedFarm] = useState<any>(null)
  const [selectedAdminAccount, setSelectedAdminAccount] = useState<any>(null)
  const [selectedCorporateFarm, setSelectedCorporateFarm] = useState<any>(null)
  const [userFarms, setUserFarms] = useState<UserFarm[]>([])
  const [isMultiFarmUser, setIsMultiFarmUser] = useState(false)
  const [isFetchingUser, setIsFetchingUser] = useState(false)
  const [farmCurrencyCode, setFarmCurrencyCode] = useState<string>('USD')
  const [exchangeRate, setExchangeRate] = useState<number | null>(1.0)

  const fetchUser = async () => {
    // Prevent concurrent fetches (important for React StrictMode double-mounting)
    if (isFetchingUser) {
      logger.debug('UserContext: Fetch already in progress, skipping')
      return
    }
    
    setIsFetchingUser(true)
    
    try {
      // ✅ ENHANCED: Handle app startup token validation and refresh
      const tokenValid = await handleAppStartup()
      if (!tokenValid) {
        logger.info('UserContext: App startup token validation failed, clearing auth data')
        await clearAllAuthData()
        setLoading(false)
        return
      }

      // ✅ ENHANCED: Use persistent storage for better PWA token persistence
      const userToken = await getAuthToken('user')
      const adminToken = await getAuthToken('admin')
      
      // If both tokens exist (which shouldn't happen after login fix), prefer the most recent
      // by checking which user info was stored more recently
      let token = null
      let userType: 'user' | 'admin' = 'user'
      
      if (userToken && adminToken) {
        // This is an error state that shouldn't occur after the login fix
        logger.warn('Both userToken and adminToken found in storage - clearing all tokens')
        await clearAllAuthData()
        setLoading(false)
        return
      } else if (adminToken) {
        token = adminToken
        userType = 'admin'
      } else if (userToken) {
        token = userToken
        userType = 'user'
      }
      
      if (!token) {
        setLoading(false)
        return
      }

      const requestCurrentUser = async (authToken: string) => fetch(createApiUrl(API_ENDPOINTS.AUTH_ME), {
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'X-Admin-Request': userType === 'admin' ? 'true' : 'false'
        },
        credentials: 'include'
      })

      let response = await requestCurrentUser(token)

      if (!response.ok && (response.status === 401 || response.status === 403)) {
        const refreshed = await tokenManager.refreshToken(userType)
        if (refreshed) {
          const refreshedToken = await getAuthToken(userType)
          if (refreshedToken) {
            token = refreshedToken
            response = await requestCurrentUser(refreshedToken)
          }
        }
      }

      if (response.ok) {
        const userData = await response.json()
        
        // Check if user is active
        if (!userData.active) {
          // User is inactive, clear all data and redirect
          await clearAllAuthData()
          setUser(null)
          setSelectedFarm(null)
          return
        }
        
        setUser(userData)
        
        // ✅ CRITICAL FIX: Save userData to localStorage so calendarType is available
        // This ensures individualAccount/corporateAccount with calendarType are persisted
        const userType = userData.userType || (adminToken ? 'admin' : 'user')
        await setUserInfo(userData, userType)
        
        // Initialize real-time notifications after ensuring a fresh token (avoids SSE 401 on startup)
        if (!notificationService.isConnected()) {
          try {
            const freshToken = await tokenManager.ensureFreshToken()
            if (freshToken) {
              notificationService.connect(freshToken)
              logger.info('Notification service connected for user:', userData.email)
            }
          } catch (error) {
            logger.error('Failed to connect notification service:', error)
          }
        }
        
        // ✅ ENHANCED: Automatically set farm for individual users ONLY
        // SUB_USERs should NOT auto-select farms - handled in fetchUserFarms
        if (userData.farm && userData.role === 'INDIVIDUAL') {
          // Regular individual user with associated farm - auto-select it
          setSelectedFarm(userData.farm)
          await persistSelectedFarm(userData.farm)
          logger.info(`Auto-selected farm for user:`, userData.farm.farmName)
        } else if (userData.role === 'ADMIN' && userData.allowAdminAccess) {
          // Admin users - restore selected admin account
          const savedAdminAccount = await getSelectedFarm()
          if (savedAdminAccount) {
            setSelectedAdminAccount(savedAdminAccount)
            logger.info(`Restored selected admin account:`, savedAdminAccount.accountName || savedAdminAccount.companyName)
          }
        } else if (userData.role === 'CORPORATE' && userData.corporateRole === 'MANAGER') {
          // Corporate managers - restore selected farm
          const savedCorporateFarm = await persistentStorage.getItem('selectedCorporateFarm')
          if (savedCorporateFarm) {
            try {
              const parsedFarm = JSON.parse(savedCorporateFarm)
              setSelectedCorporateFarm(parsedFarm)
              logger.info(`Restored selected corporate farm:`, parsedFarm.farm?.name || parsedFarm.accountName)
            } catch (error) {
              logger.error('Error parsing saved corporate farm:', error)
            }
          }
        } else if (userData.role === 'SUB_USER' && userData.corporateRole === 'MEMBER') {
          // Corporate employees - don't auto-restore farm selection, they should start on corporate dashboard
          setSelectedCorporateFarm(null)
          logger.info(`Corporate employee logged in - starting on corporate dashboard`)
        } else if (userData.role === 'SUB_USER') {
          const savedFarm = await getSelectedFarm()
          if (savedFarm) {
            setSelectedFarm(savedFarm)
            logger.info(`Restored selected farm for sub-user:`, savedFarm.farmName)
          }
        } else if (userData.userType === 'consultant' || userData.userType === 'admin') {
          // Consultant users - restore selected farm
          const savedFarm = await getSelectedFarm()
          if (savedFarm) {
            setSelectedFarm(savedFarm)
            logger.info(`Restored selected farm for ${userData.userType}:`, savedFarm.farmName)
          }
        }
      } else {
        // Token might be invalid, clear all auth data
        await clearAllAuthData()
        setUser(null)
        setSelectedFarm(null)
      }
    } catch (error) {
      // Check if this is a network error (offline) vs authentication error
      const isNetworkError = error instanceof TypeError && error.message.includes('fetch')
      const isOffline = !navigator.onLine
      
      // Only log as error if it's not a network/offline issue
      if (isNetworkError || isOffline) {
        logger.warn('Network error or offline - preserving user state:', error)
      } else {
        logger.error('Error fetching user:', error)
      }
      
      if (isNetworkError || isOffline) {
        // User is offline - preserve existing user state if available
        const savedUserInfo = await getUserInfo('user')
        const savedAdminInfo = await getUserInfo('admin')
        const savedUser = savedUserInfo || savedAdminInfo
        
        if (savedUser) {
          setUser(savedUser)
          logger.info('Preserved user state while offline')
          
          // ✅ CONNECT NOTIFICATION SERVICE: Initialize real-time notifications for offline user
          try {
            const offlineToken = await tokenManager.ensureFreshToken()
            if (offlineToken && !notificationService.isConnected()) {
              notificationService.connect(offlineToken)
              logger.info('Notification service connected for offline user:', savedUser.email)
            }
          } catch (error) {
            logger.error('Failed to connect notification service for offline user:', error)
          }
          
          // Auto-select farm for individual users or restore admin/corporate selection
          if (savedUser.farm) {
            setSelectedFarm(savedUser.farm)
          } else if (savedUser.role === 'CORPORATE') {
            // Restore corporate farm selection
            const savedCorporateFarm = await persistentStorage.getItem('selectedCorporateFarm')
            if (savedCorporateFarm) {
              try {
                const parsedFarm = JSON.parse(savedCorporateFarm)
                setSelectedCorporateFarm(parsedFarm)
              } catch (error) {
                logger.error('Error parsing saved corporate farm while offline:', error)
              }
            }
          } else {
            // Also restore selected farm/admin account if available
            const savedFarm = await getSelectedFarm()
            if (savedFarm) {
              if (savedUser.role === 'ADMIN' && savedUser.allowAdminAccess) {
                setSelectedAdminAccount(savedFarm)
              } else {
                setSelectedFarm(savedFarm)
              }
            }
          }
        }
      } else {
        // This is likely an authentication error - clear user state
        await clearAllAuthData()
        setUser(null)
        setSelectedFarm(null)
      }
    } finally {
      setLoading(false)
      setIsFetchingUser(false)
    }
  }

  const fetchUserFarms = async () => {
    try {
      const userToken = await getAuthToken('user')
      if (!userToken || !user) {
        return
      }

      // Only fetch farms for SUB_USER and INDIVIDUAL roles
      if (user.role === 'SUB_USER' || user.role === 'INDIVIDUAL') {
        const response = await fetch(createApiUrl('/api/users/my-farms'), {
          headers: {
            'Authorization': `Bearer ${userToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          const farms = data.farms || []
          setUserFarms(farms)
          setIsMultiFarmUser(farms.length > 1)
          
          // Handle farm selection based on number of farms
          if (farms.length > 0 && !selectedFarm) {
            const savedFarm = await persistentStorage.getSelectedFarm()
            
            if (farms.length === 1) {
              const onlyFarm = {
                farmId: farms[0].farmId,
                farmName: farms[0].farmName,
                accountId: farms[0].accountId,
                accountName: farms[0].accountName
              }
              await handleSetSelectedFarm(onlyFarm)
            } else if (savedFarm && farms.some((f: UserFarm) => f.farmId === savedFarm.farmId)) {
              // Multi-farm user with saved selection: restore it
              await handleSetSelectedFarm(savedFarm)
            }
            // Multi-farm user without saved selection: don't auto-select, show farm selector dashboard
          }
        }
      }
    } catch (error) {
      logger.error('Error fetching user farms:', error)
    }
  }

  useEffect(() => {
    fetchUser()
    tokenManager.startTokenCheck()
  }, [])

  // Fetch user farms when user is loaded
  useEffect(() => {
    if (user && (user.role === 'SUB_USER' || user.role === 'INDIVIDUAL')) {
      fetchUserFarms()
    }
  }, [user?.id])

  const handleSetSelectedFarm = async (farm: any) => {
    setSelectedFarm(farm)
    if (farm) {
      await persistSelectedFarm(farm)
    } else {
      await persistentStorage.removeItem('selectedFarm')
      setFarmCurrencyCode('USD')
      setExchangeRate(1.0)
    }
  }

  // Effect to fetch currency when selectedFarm changes
  useEffect(() => {
    const fetchFarmCurrency = async () => {
      const currentFarm = selectedFarm || selectedCorporateFarm?.farm
      if (!currentFarm) {
        setFarmCurrencyCode('USD')
        setExchangeRate(1.0)
        return
      }

      try {
        const farmId = currentFarm.farmId || currentFarm.id
        if (farmId) {
          const currencyData = await currencyService.getFarmCurrency(farmId)
          setFarmCurrencyCode(currencyData.currencyCode || 'USD')
          
          try {
            const rateData = await currencyService.getFarmCurrencyRate(farmId)
            setExchangeRate(rateData.rate)
          } catch (error) {
            // If rate not found, default to 1.0 for USD
            if (currencyData.currencyCode === 'USD') {
              setExchangeRate(1.0)
            } else {
              setExchangeRate(null)
            }
          }
        }
      } catch (error) {
        logger.error('Error fetching farm currency:', error)
        setFarmCurrencyCode('USD')
        setExchangeRate(1.0)
      }
    }

    fetchFarmCurrency()
  }, [selectedFarm, selectedCorporateFarm])

  const handleSetSelectedAdminAccount = async (account: any) => {
    setSelectedAdminAccount(account)
    if (account) {
      const normalized =
        account.type === 'individual' && account.farm?.id
          ? {
              ...account,
              farmId: account.farm.id,
              farmName: account.farm.name || account.accountName,
            }
          : account
      await persistSelectedFarm(normalized)
    } else {
      await persistentStorage.removeItem('selectedFarm')
    }
  }

  const handleSetSelectedCorporateFarm = async (farm: any) => {
    setSelectedCorporateFarm(farm)
    if (farm) {
      await persistentStorage.setItem('selectedCorporateFarm', JSON.stringify(farm))
    } else {
      await persistentStorage.removeItem('selectedCorporateFarm')
    }
  }

  const login = async (email: string, password: string, rememberMe = true) => {
    try {
      const res = await fetch(createApiUrl(API_ENDPOINTS.AUTH_LOGIN), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify({ email, password, rememberMe }),
      })
      
      const data = await res.json()
      
      if (!res.ok) {
        throw new Error(data.error || "Login failed")
      }
      
      logger.info("Login successful", { 
        userType: data.user?.userType, 
        hasToken: !!data.token 
      })
      
      await clearAllAuthData()
      
      const sessionType = data.user.userType === 'admin' ? 'admin' : 'user'
      await persistSessionTokens(data.token, sessionType, data.refreshToken)
      await setUserInfo(data.user, sessionType)
      tokenManager.startTokenCheck()
      
      await fetchUser() // Refresh the user context with new data (also connects notifications)
      
    } catch (err: any) {
      logger.error("Login error:", err)
      throw new Error(err.message || 'Login failed')
    }
  }

  const logout = async () => {
    // ✅ DISCONNECT NOTIFICATION SERVICE: Clean up real-time notifications
    try {
      notificationService.disconnect()
      logger.info('Notification service disconnected on logout')
    } catch (error) {
      logger.error('Error disconnecting notification service:', error)
    }
    
    // Notify server to clear HttpOnly refresh cookie
    try { 
      await fetch(createApiUrl(API_ENDPOINTS.AUTH_LOGOUT), { 
        method: 'POST', 
        credentials: 'include' 
      }) 
    } catch {}
    
    // Clear all auth data from persistent storage
    await clearAllAuthData()
    setUser(null)
    setSelectedFarm(null)
    setSelectedAdminAccount(null)
    setSelectedCorporateFarm(null)
    setLoading(false)
    
    // Enhanced security: Force page reload for admin users
    if (user?.userType === 'admin') {
      window.location.reload()
    }
  }

  // Computed properties
  const isAdmin = user?.role === 'ADMIN' && user?.allowAdminAccess
  // isConsultant now maps to SUB_USER role (team members with multi-farm access)
  const isSubUser = user?.role === 'SUB_USER'
  const isConsultant = isSubUser // Backward compatibility
  const isFarmer = user?.role === 'INDIVIDUAL' && !!user?.farmId
  const isCorporateManager = user?.role === 'CORPORATE' && user?.corporateRole === 'MANAGER'
  // Corporate members include:
  // 1. Users with role='CORPORATE' and corporateRole='MEMBER' (corporate account owners)
  // 2. Users with role='SUB_USER' and corporateRole='MEMBER' (corporate employees)
  const isCorporateMember = (user?.role === 'CORPORATE' && user?.corporateRole === 'MEMBER') || 
                           (user?.role === 'SUB_USER' && user?.corporateRole === 'MEMBER')

  const isCorporate = user?.role === 'CORPORATE' || isCorporateManager || isCorporateMember
  const isAdminViewingCorporate = !!(isAdmin && selectedAdminAccount?.type === 'corporate')

  const effectiveFarmId =
    selectedCorporateFarm?.farm?.id ||
    selectedAdminAccount?.farm?.id ||
    selectedFarm?.farm?.id ||
    selectedFarm?.farmId ||
    selectedFarm?.id ||
    user?.farmId ||
    null

  const effectiveCorporateAccountId = isCorporate
    ? (user?.corporateAccountId || null)
    : (isAdminViewingCorporate ? selectedAdminAccount.id : null)

  return (
    <UserContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      selectedFarm, 
      setSelectedFarm: handleSetSelectedFarm, 
      selectedAdminAccount,
      setSelectedAdminAccount: handleSetSelectedAdminAccount,
      selectedCorporateFarm,
      setSelectedCorporateFarm: handleSetSelectedCorporateFarm,
      isAdmin, 
      isConsultant, 
      isFarmer,
      isCorporateManager,
      isCorporateMember,
      isCorporate,
      effectiveFarmId,
      effectiveCorporateAccountId,
      isAdminViewingCorporate,
      refreshUser: fetchUser,
      userFarms,
      isMultiFarmUser,
      fetchUserFarms,
      farmCurrencyCode,
      exchangeRate
    }}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
